import { useState, useCallback } from 'react';
import { ethers } from 'ethers';
import MarketSettlementABI from '../abi/MarketSettlement.json';
import { TxState } from './usePredictionMarket';

const SETTLEMENT_CONTRACT_ADDRESS =
  process.env.REACT_APP_SETTLEMENT_CONTRACT_ADDRESS || '';

function getProvider(): ethers.BrowserProvider {
  const eth = (window as any).ethereum;
  if (!eth) throw new Error('MetaMask not detected. Please install MetaMask.');
  return new ethers.BrowserProvider(eth);
}

async function getSignerAndContract(address: string) {
  if (!address) throw new Error('Settlement contract address not configured. Set REACT_APP_SETTLEMENT_CONTRACT_ADDRESS.');
  const provider = getProvider();
  const signer = await provider.getSigner();
  const contract = new ethers.Contract(address, MarketSettlementABI, signer);
  return { signer, contract };
}

export interface SettlementResolution {
  marketId: number;
  outcome: boolean;
  price: string;      // formatted from wei
  resolver: string;
  evidenceHash: string;
  timestamp: number;
}

export interface AuditLogEntry {
  timestamp: Date;
  action: 'approve' | 'manual' | 'reject';
  marketId: number;
  outcome?: boolean;
  note?: string;
  txHash?: string;
}

export function useMarketSettlement() {
  const [tx, setTx] = useState<TxState>({ status: 'idle', hash: null, error: null });
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);

  const resetTx = useCallback(() =>
    setTx({ status: 'idle', hash: null, error: null }), []);

  const addAuditEntry = useCallback((entry: AuditLogEntry) => {
    setAuditLog(prev => [entry, ...prev]);
  }, []);

  /**
   * Approve a bot-submitted settlement by calling resolveMarket on the
   * MarketSettlement contract without a separate off-chain signature.
   */
  const approveSettlement = useCallback(
    async (
      marketId: number,
      outcome: boolean,
      price: string,
      evidenceHash: string,
    ): Promise<boolean> => {
      setTx({ status: 'pending', hash: null, error: null });
      try {
        const { contract } = await getSignerAndContract(SETTLEMENT_CONTRACT_ADDRESS);
        const priceWei = price ? BigInt(price) : BigInt(0);
        const evidenceBytes32 = evidenceHash
          ? ethers.zeroPadValue(ethers.toUtf8Bytes(evidenceHash), 32)
          : ethers.ZeroHash;
        const txResp = await contract.resolveMarket(
          BigInt(marketId),
          outcome,
          priceWei,
          '0x',
          evidenceBytes32,
        );
        setTx({ status: 'pending', hash: txResp.hash, error: null });
        await txResp.wait();
        setTx({ status: 'success', hash: txResp.hash, error: null });
        addAuditEntry({
          timestamp: new Date(),
          action: 'approve',
          marketId,
          outcome,
          txHash: txResp.hash,
        });
        return true;
      } catch (err: any) {
        const msg = err?.reason ?? err?.message ?? 'Unknown error';
        setTx({ status: 'error', hash: null, error: msg });
        return false;
      }
    },
    [addAuditEntry],
  );

  /**
   * Manually submit a settlement with full parameters including an optional
   * ECDSA signature from the admin/oracle key.
   */
  const manualSettlement = useCallback(
    async (
      marketId: number,
      outcome: boolean,
      price: string,
      signature: string,
      evidenceHash: string,
    ): Promise<boolean> => {
      setTx({ status: 'pending', hash: null, error: null });
      try {
        const { contract } = await getSignerAndContract(SETTLEMENT_CONTRACT_ADDRESS);
        const priceWei = price ? BigInt(price) : BigInt(0);
        const evidenceBytes32 = evidenceHash
          ? ethers.zeroPadValue(ethers.toUtf8Bytes(evidenceHash), 32)
          : ethers.ZeroHash;
        const sigBytes = signature && signature !== '0x' ? signature : '0x';
        const txResp = await contract.resolveMarket(
          BigInt(marketId),
          outcome,
          priceWei,
          sigBytes,
          evidenceBytes32,
        );
        setTx({ status: 'pending', hash: txResp.hash, error: null });
        await txResp.wait();
        setTx({ status: 'success', hash: txResp.hash, error: null });
        addAuditEntry({
          timestamp: new Date(),
          action: 'manual',
          marketId,
          outcome,
          txHash: txResp.hash,
        });
        return true;
      } catch (err: any) {
        const msg = err?.reason ?? err?.message ?? 'Unknown error';
        setTx({ status: 'error', hash: null, error: msg });
        return false;
      }
    },
    [addAuditEntry],
  );

  /**
   * Log a rejection/challenge for audit purposes (no on-chain transaction).
   */
  const rejectSettlement = useCallback(
    (marketId: number, note: string) => {
      addAuditEntry({
        timestamp: new Date(),
        action: 'reject',
        marketId,
        note,
      });
    },
    [addAuditEntry],
  );

  return {
    tx,
    resetTx,
    auditLog,
    approveSettlement,
    manualSettlement,
    rejectSettlement,
  };
}
